import {
  Account,
  PammTauroMarketsAccount,
  PammTauroMarketsMauritiusAccount,
  PammTauroMarketsMt5Account,
} from '../../../../../graphql/types'
import {getMetadataFromAccount} from '../../../AccountCard/utils'

export type PammInvestorAccount =
  | PammTauroMarketsAccount
  | PammTauroMarketsMauritiusAccount
  | PammTauroMarketsMt5Account

export type PammInvestorCardState = {
  linkedForexAccount: Account | null
  linkedMtBalance: number
  investorBalance: number
  minimumDeposit: number
  isDepositNeeded: boolean
  isTransferNeeded: boolean
  isUnapproved: boolean
}

/**
 * Derives everything the PAMM investor card and its pop-ups need from the account and its siblings.
 *
 * Two balances are in play and they are not interchangeable (PT-5774):
 * - `investorBalance` — the PAMM investor account's own balance. This is the one the server counts
 *   towards the offer minimum, so it is the one to show the client next to that minimum.
 * - `linkedMtBalance` — the balance of the linked trading account. Clients funded during the
 *   PT-5568 flow still hold money there, so it only decides whether a transfer is the way in.
 *
 * `minimumDeposit` already arrives as the shortfall the *next* deposit has to meet, net of the
 * investor balance (PT-5773), so it is quoted as-is and never has a balance subtracted from it.
 */
export function getPammInvestorCardState(
  account: PammInvestorAccount,
  restAccounts: Account[],
): PammInvestorCardState {
  const accountMetadata = getMetadataFromAccount(account)
  const relatedForexAccountId = accountMetadata.relatedForexAccountId as number | undefined
  const linkedForexAccount = relatedForexAccountId
    ? restAccounts.find(({id}) => id === relatedForexAccountId) ?? null
    : null
  const linkedMtBalance = linkedForexAccount?.balance ?? 0
  const investorBalance = account.balance ?? 0
  const minimumDeposit = account.minimumDeposit ?? 0

  // Money already sitting on the linked account covers the whole remaining minimum, so it is moved
  // across instead of asking the client to pay again.
  const isTransferNeeded = Boolean(account.unconnected && linkedMtBalance >= minimumDeposit)
  const isDepositNeeded = Boolean(account.unconnected && !isTransferNeeded)
  const isUnapproved = Boolean(
    !account.unconnected && !account.isPammInvestorAccountApprovedByMoneyManager,
  )

  return {
    linkedForexAccount,
    linkedMtBalance,
    investorBalance,
    minimumDeposit,
    isDepositNeeded,
    isTransferNeeded,
    isUnapproved,
  }
}
